import * as React from 'react';
import { createPortal } from 'react-dom';
import { DatePicker as SitDatePicker, DateRange } from 'Src/components/DatePicker/DatePicker';
import './ParentFilterBar.scss';

type StatusOption = { value: string; label: string };

type Props = {
  filters: any;
  onTableFilterChange: (event, field: string, options?: any) => void;
  onApply: () => void;
  onClear: () => void;
  onCancel: () => void;
  hint?: React.ReactNode;
  extraFilters?: React.ReactNode;

  labels?: {
    text?: string;
    secondText?: string;
    date?: string;
    secondDate?: string;
    status?: string;
    apply?: string;
    clear?: string;
  };
  fieldKeys?: {
    textKey?: string;
    secondTextKey?: string;
    dateKey?: string;
    secondDateKey?: string;
    statusKey?: string;
  };

  showStatus?: boolean;
  statusOptions?: StatusOption[];
  showTextFilter?: boolean;
};

export const ParentFilterBar: React.FC<Props> = ({
  filters,
  onTableFilterChange,
  onApply,
  onClear,
  onCancel,
  hint,
  extraFilters,
  labels,
  fieldKeys,
  showStatus,
  statusOptions,
  showTextFilter = true
}) => {
  const drawerRef = React.useRef<HTMLElement>(null);
  const textKey = fieldKeys?.textKey ?? 'awardType.name';
  const secondTextKey = fieldKeys?.secondTextKey ?? undefined;
  const dateKey = fieldKeys?.dateKey ?? 'awardedAt';
  const secondDateKey = fieldKeys?.secondDateKey;
  const statusKey = fieldKeys?.statusKey ?? 'isTakePart';

  const textLabel = labels?.text ?? 'Award type';
  const secondTextLabel = labels?.secondText ?? 'Second text';
  const dateLabel = labels?.date ?? 'Awarded date';
  const secondDateLabel = labels?.secondDate ?? 'Second date';
  const statusLabel = labels?.status ?? 'Participation';
  const applyLabel = labels?.apply ?? 'Apply';
  const clearLabel = labels?.clear ?? 'Clear';

  const defaultStatusOptions: StatusOption[] = [
    { value: 'all', label: 'All' },
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' }
  ];
  const effectiveStatusOptions = statusOptions && statusOptions.length ? statusOptions : defaultStatusOptions;

  const currentStatusValue = String(
    typeof filters[statusKey] !== 'undefined' ? filters[statusKey] : effectiveStatusOptions[0]?.value ?? 'all'
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onApply();
      onCancel();
    }
  };

  const dateValue = (key: string): DateRange => ({
    start: filters[key]?.from ? new Date(`${filters[key].from}T00:00:00`) : null,
    end: filters[key]?.to ? new Date(`${filters[key].to}T00:00:00`) : null
  });
  const setDateValue = (key: string, value: DateRange | Date | null) => {
    const range = value && !(value instanceof Date) ? value : { start: value, end: value };
    const two = (value: number) => (value < 10 ? `0${value}` : String(value));
    const iso = (date: any) =>
      date instanceof Date && !Number.isNaN(date.getTime())
        ? `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())}`
        : '';
    onTableFilterChange({ target: { value: { from: iso(range?.start), to: iso(range?.end) } } }, key);
  };

  React.useEffect(() => {
    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };

    document.addEventListener('keydown', onDocumentKeyDown);
    return () => document.removeEventListener('keydown', onDocumentKeyDown);
  }, [onCancel]);

  React.useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (drawerRef.current && !drawerRef.current.contains(target)) {
        onCancel();
      }
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [onCancel]);

  return createPortal(
    <>
      <button type="button" className="parentFilterDrawer__backdrop" aria-label="Close filters" onClick={onCancel} />
      <aside
        ref={drawerRef}
        className="parentFilterDrawer parentStudentFilter people-filter"
        role="dialog"
        aria-modal="true"
        aria-labelledby="parent-filter-title"
      >
        <header className="parentFilterDrawer__header people-filter__header">
          <div>
            <h2 id="parent-filter-title">Filters</h2>
            <p>Narrow the list using one or more fields.</p>
          </div>
          <button type="button" className="parentFilterDrawer__close" aria-label="Close filters" onClick={onCancel}>
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div className="parentFilterDrawer__body people-filter__body">
          {hint ? <div className="parentFilterDrawer__hint">{hint}</div> : null}
          {showTextFilter && (
            <div className="parentFilterDrawer__field people-filter__field">
              <label className="form-label">{textLabel}</label>
              <input
                type="text"
                className="form-control"
                placeholder={`Search by ${textLabel.toLowerCase()}…`}
                value={filters[textKey] || ''}
                onChange={e => onTableFilterChange(e, textKey)}
                onKeyDown={onKeyDown}
              />
            </div>
          )}

          {secondTextKey && (
            <div className="parentFilterDrawer__field people-filter__field">
              <label className="form-label">{secondTextLabel}</label>
              <input
                type="text"
                className="form-control"
                placeholder={`Search by ${secondTextLabel.toLowerCase()}…`}
                value={filters[secondTextKey] || ''}
                onChange={e => onTableFilterChange(e, secondTextKey)}
                onKeyDown={onKeyDown}
              />
            </div>
          )}

          <div className="parentFilterDrawer__field people-filter__field people-filter__dateRange">
            <label className="form-label">{dateLabel}</label>
            <SitDatePicker
              mode="range"
              centered
              value={dateValue(dateKey)}
              onChange={value => setDateValue(dateKey, value)}
              format="DD-MM-YYYY"
              placeholder="Select date range"
              numberOfMonths={2}
              clearable={true}
            />
          </div>

          {showStatus && (
            <div className="parentFilterDrawer__field people-filter__field">
              <label className="form-label">{statusLabel}</label>
              <div className="parentFilterDrawer__chips" role="group" aria-label={statusLabel}>
                {effectiveStatusOptions.map(option => (
                  <button
                    type="button"
                    key={option.value || 'all'}
                    className={currentStatusValue === option.value ? 'is-selected' : ''}
                    onClick={() => onTableFilterChange({ target: { value: option.value } }, statusKey)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {secondDateKey ? (
            <div className="parentFilterDrawer__field people-filter__field people-filter__dateRange">
              <label className="form-label">{secondDateLabel}</label>
              <SitDatePicker
                mode="range"
                centered
                value={dateValue(secondDateKey)}
                onChange={value => setDateValue(secondDateKey, value)}
                format="DD-MM-YYYY"
                placeholder="Select date range"
                numberOfMonths={2}
                clearable={true}
              />
            </div>
          ) : null}
          {extraFilters}
        </div>

        <footer className="parentFilterDrawer__footer people-filter__actions">
          <button
            type="button"
            className="parentFilterDrawer__clear people-btn people-btn--ghost"
            onClick={() => {
              onClear();
              onCancel();
            }}
          >
            {clearLabel}
          </button>
          <div>
            <button
              type="button"
              className="parentFilterDrawer__cancel people-btn people-btn--secondary"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="parentFilterDrawer__apply people-btn people-btn--primary"
              onClick={() => {
                onApply();
                onCancel();
              }}
            >
              {applyLabel}
            </button>
          </div>
        </footer>
      </aside>
    </>,
    document.body
  );
};
